import { StrictMode, lazy } from "react";
import { createRoot } from "react-dom/client";
import { RouterProvider, createBrowserRouter } from "react-router";
import { ClerkProvider } from "@clerk/clerk-react";
import { dark } from "@clerk/themes";
import { Toaster } from "@/components/ui/sonner";
import { ThemeProvider } from "@/components/theme-provider";
import "@/styles/globals.css";

import { SpaRoot } from "./root";
import { AppLayout } from "./app-layout";
import { HomeRedirect } from "./routes/home";
import { NotFoundRoute } from "./routes/not-found";
import { withParams } from "./routes/_with-params";

// Route-level code splitting. Each `lazy(() => import(...))` produces
// its own chunk in the rolldown output, so first paint only ships
// the shell + the route the user landed on. Vite also automatically
// chunks shared deps (mapbox-gl, lucide-react, etc.) on first use,
// so the per-route chunks stay small.
//
// Suspense is mounted inside <AppLayout> so the chrome (sidebar,
// header, command bar) paints immediately while the route bundle
// streams in.

// Auth flows. These are tiny and load on the cold path before the
// app shell, so eager-loading them is fine.
import { SignInRoute } from "./routes/sign-in";
import { SignUpRoute } from "./routes/sign-up";

// Public customer tracking page. Lives OUTSIDE the AppLayout (no
// sidebar, no Clerk session, no command bar) — the URL token is the
// entire auth surface. Lazy-loaded so the dispatcher bundle doesn't
// ship the customer-facing page on first paint. The route adapter
// already calls withParams to bridge react-router's :token segment
// onto the Next-shaped page component.
const TrackRoute = lazy(() =>
  import("./routes/track").then((m) => ({ default: m.TrackRoute })),
);

// Top-level routes (in Next App Router these lived in `(app)/`).
// `lazy(() => import(...).then(m => ({ default: m.<Name>Route })))`
// is the canonical shape because React.lazy requires the module
// shape `{ default: Component }` but our adapter files re-export
// named symbols.
const DashboardRoute = lazy(() =>
  import("./routes/dashboard").then((m) => ({ default: m.DashboardRoute })),
);
const ExceptionsRoute = lazy(() =>
  import("./routes/exceptions").then((m) => ({ default: m.ExceptionsRoute })),
);
const LoadsRoute = lazy(() =>
  import("./routes/loads").then((m) => ({ default: m.LoadsRoute })),
);
const OrdersRoute = lazy(() =>
  import("./routes/orders").then((m) => ({ default: m.OrdersRoute })),
);
const StrategiesRoute = lazy(() =>
  import("./routes/strategies").then((m) => ({ default: m.StrategiesRoute })),
);
const WorkflowsRoute = lazy(() =>
  import("./routes/workflows").then((m) => ({ default: m.WorkflowsRoute })),
);
const CostsRoute = lazy(() =>
  import("./routes/costs").then((m) => ({ default: m.CostsRoute })),
);
const PayoutsRoute = lazy(() =>
  import("./routes/payouts").then((m) => ({ default: m.PayoutsRoute })),
);
const KeyboardShortcutsRoute = lazy(() =>
  import("./routes/keyboard-shortcuts").then((m) => ({
    default: m.KeyboardShortcutsRoute,
  })),
);

// Pricing.
const PricingRoute = lazy(() =>
  import("./routes/pricing").then((m) => ({ default: m.PricingRoute })),
);
const PricingConstantsRoute = lazy(() =>
  import("./routes/pricing-constants").then((m) => ({
    default: m.PricingConstantsRoute,
  })),
);
const PricingDraftsRoute = lazy(() =>
  import("./routes/pricing-drafts").then((m) => ({
    default: m.PricingDraftsRoute,
  })),
);
const PricingModifiersRoute = lazy(() =>
  import("./routes/pricing-modifiers").then((m) => ({
    default: m.PricingModifiersRoute,
  })),
);

// Fleet.
const FleetDriversRoute = lazy(() =>
  import("./routes/fleet-drivers").then((m) => ({
    default: m.FleetDriversRoute,
  })),
);
const FleetDriversNewRoute = lazy(() =>
  import("./routes/fleet-drivers-new").then((m) => ({
    default: m.FleetDriversNewRoute,
  })),
);
const FleetVehiclesRoute = lazy(() =>
  import("./routes/fleet-vehicles").then((m) => ({
    default: m.FleetVehiclesRoute,
  })),
);
const FleetVehiclesNewRoute = lazy(() =>
  import("./routes/fleet-vehicles-new").then((m) => ({
    default: m.FleetVehiclesNewRoute,
  })),
);

// Telematics.
const TelematicsRoute = lazy(() =>
  import("./routes/telematics").then((m) => ({ default: m.TelematicsRoute })),
);
const TelematicsSourcesNewRoute = lazy(() =>
  import("./routes/telematics-sources-new").then((m) => ({
    default: m.TelematicsSourcesNewRoute,
  })),
);

// Ingestion.
const IngestionRoute = lazy(() =>
  import("./routes/ingestion").then((m) => ({ default: m.IngestionRoute })),
);
const IngestionUploadRoute = lazy(() =>
  import("./routes/ingestion-upload").then((m) => ({
    default: m.IngestionUploadRoute,
  })),
);
const IngestionRunsIdRoute = lazy(() =>
  import("./routes/ingestion-runs-id").then((m) => ({
    default: withParams<{ id: string }>(m.IngestionRunsIdRoute),
  })),
);

// Billing.
const BillingRoute = lazy(() =>
  import("./routes/billing").then((m) => ({ default: m.BillingRoute })),
);
const BillingInvoicesIdRoute = lazy(() =>
  import("./routes/billing-invoices-id").then((m) => ({
    default: withParams<{ id: string }>(m.BillingInvoicesIdRoute),
  })),
);
const BillingArAgingRoute = lazy(() =>
  import("./routes/billing-ar-aging").then((m) => ({
    default: m.BillingArAgingRoute,
  })),
);

// Customers. The /customers list still lives behind the global
// search + sidebar (?id=…); these are the deep-linked detail and
// billing sub-pages.
const CustomerDetailRoute = lazy(() =>
  import("./routes/customer-detail").then((m) => ({
    default: withParams<{ id: string }>(m.CustomerDetailRoute),
  })),
);
const CustomerBillingRoute = lazy(() =>
  import("./routes/customer-billing").then((m) => ({
    default: withParams<{ id: string }>(m.CustomerBillingRoute),
  })),
);

// Brokers.
const BrokersRoute = lazy(() =>
  import("./routes/brokers").then((m) => ({ default: m.BrokersRoute })),
);
const BrokersIdRoute = lazy(() =>
  import("./routes/brokers-id").then((m) => ({
    default: withParams<{ id: string }>(m.BrokersIdRoute),
  })),
);
const BrokersTendersIdRoute = lazy(() =>
  import("./routes/brokers-tenders-id").then((m) => ({
    default: withParams<{ id: string }>(m.BrokersTendersIdRoute),
  })),
);

// Settings.
const SettingsRoute = lazy(() =>
  import("./routes/settings").then((m) => ({ default: m.SettingsRoute })),
);
const SettingsTeamRoute = lazy(() =>
  import("./routes/settings-team").then((m) => ({
    default: m.SettingsTeamRoute,
  })),
);
const SettingsApiKeysRoute = lazy(() =>
  import("./routes/settings-api-keys").then((m) => ({
    default: m.SettingsApiKeysRoute,
  })),
);
const SettingsBranchesRoute = lazy(() =>
  import("./routes/settings-branches").then((m) => ({
    default: m.SettingsBranchesRoute,
  })),
);
const SettingsRegionsRoute = lazy(() =>
  import("./routes/settings-regions").then((m) => ({
    default: m.SettingsRegionsRoute,
  })),
);
const SettingsSimulationRoute = lazy(() =>
  import("./routes/settings-simulation").then((m) => ({
    default: m.SettingsSimulationRoute,
  })),
);
const SettingsFulfillmentRoute = lazy(() =>
  import("./routes/settings-fulfillment").then((m) => ({
    default: m.SettingsFulfillmentRoute,
  })),
);

const router = createBrowserRouter([
  {
    path: "/",
    element: <SpaRoot />,
    children: [
      { index: true, element: <HomeRedirect /> },
      { path: "sign-in/*", element: <SignInRoute /> },
      { path: "sign-up/*", element: <SignUpRoute /> },
      // Customer-facing tracking. Intentionally outside <AppLayout>
      // — no Clerk session, no sidebar. The opaque token in the URL
      // is the entire scope; the backend resolves it at
      // /api/v1/public/track/:token.
      { path: "track/:token", element: <TrackRoute /> },
      {
        path: "",
        element: <AppLayout />,
        children: [
          { path: "dashboard", element: <DashboardRoute /> },
          { path: "exceptions", element: <ExceptionsRoute /> },
          { path: "loads", element: <LoadsRoute /> },
          { path: "orders", element: <OrdersRoute /> },
          { path: "strategies", element: <StrategiesRoute /> },
          { path: "workflows", element: <WorkflowsRoute /> },
          { path: "costs", element: <CostsRoute /> },
          { path: "payouts", element: <PayoutsRoute /> },
          { path: "keyboard-shortcuts", element: <KeyboardShortcutsRoute /> },
          { path: "pricing", element: <PricingRoute /> },
          { path: "pricing/constants", element: <PricingConstantsRoute /> },
          { path: "pricing/drafts", element: <PricingDraftsRoute /> },
          { path: "pricing/modifiers", element: <PricingModifiersRoute /> },
          { path: "fleet/drivers", element: <FleetDriversRoute /> },
          { path: "fleet/drivers/new", element: <FleetDriversNewRoute /> },
          { path: "fleet/vehicles", element: <FleetVehiclesRoute /> },
          { path: "fleet/vehicles/new", element: <FleetVehiclesNewRoute /> },
          { path: "telematics", element: <TelematicsRoute /> },
          { path: "telematics/sources/new", element: <TelematicsSourcesNewRoute /> },
          { path: "ingestion", element: <IngestionRoute /> },
          { path: "ingestion/upload", element: <IngestionUploadRoute /> },
          // React Router uses :id where Next used [id]; the underlying
          // page reads via useParams (forwarded through the
          // next-navigation shim).
          { path: "ingestion/runs/:id", element: <IngestionRunsIdRoute /> },
          { path: "billing", element: <BillingRoute /> },
          { path: "billing/invoices/:id", element: <BillingInvoicesIdRoute /> },
          { path: "billing/ar-aging", element: <BillingArAgingRoute /> },
          { path: "customers/:id", element: <CustomerDetailRoute /> },
          {
            path: "customers/:id/billing",
            element: <CustomerBillingRoute />,
          },
          { path: "brokers", element: <BrokersRoute /> },
          { path: "brokers/:id", element: <BrokersIdRoute /> },
          { path: "brokers/tenders/:id", element: <BrokersTendersIdRoute /> },
          { path: "settings", element: <SettingsRoute /> },
          { path: "settings/team", element: <SettingsTeamRoute /> },
          { path: "settings/api-keys", element: <SettingsApiKeysRoute /> },
          { path: "settings/branches", element: <SettingsBranchesRoute /> },
          { path: "settings/regions", element: <SettingsRegionsRoute /> },
          { path: "settings/simulation", element: <SettingsSimulationRoute /> },
          {
            path: "settings/fulfillment",
            element: <SettingsFulfillmentRoute />,
          },
        ],
      },
      { path: "*", element: <NotFoundRoute /> },
    ],
  },
]);

const publishableKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;
if (!publishableKey) {
  throw new Error("VITE_CLERK_PUBLISHABLE_KEY is required");
}

const container = document.getElementById("root");
if (!container) throw new Error("#root not found");

createRoot(container).render(
  <StrictMode>
    <ClerkProvider
      publishableKey={publishableKey}
      appearance={{ baseTheme: dark }}
      signInUrl="/sign-in"
      signUpUrl="/sign-up"
      signInFallbackRedirectUrl="/dashboard"
      signUpFallbackRedirectUrl="/dashboard"
    >
      <ThemeProvider>
        <RouterProvider router={router} />
      </ThemeProvider>
      <Toaster richColors />
    </ClerkProvider>
  </StrictMode>,
);
